import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs, useMediaQuery } from "../../Styles";
import { ConfirmModal, MainContentPaddingTypes, NewButton, NewModal } from "..";
import { ButtonTypes, SystemTypes } from "../../Definitions";
import { useState } from "react";
import { DeviceInfo } from "../../Services";

export interface BaseIO {
  offset: number;
  terminal_id: number;
  text: string;
  port: string | number;
  sensor_p?: string | number;
  boardDisabled: boolean;
  board: DeviceInfo | undefined;
  programmedDisabled: boolean;
  programmed: number;
  components?: number;
  calibrationVal?: number;
}

interface NewSectionModal_Props {
  focus_view?: boolean;
  systemType: SystemTypes;
  subHeader: string;
  inputArea: JSX.Element;
  activeArea?: (JSX.Element | null)[] | null;
  selectArea: JSX.Element | undefined;
  footerMessage: string;
  onSave: () => void;
  onClose: () => void;
  onDeleteConfirm: () => void;
}

export default function EditSectionModal({ focus_view = false, systemType, subHeader, inputArea, activeArea, selectArea, footerMessage, onSave, onClose, onDeleteConfirm }: NewSectionModal_Props) {
  const { t } = useTranslation();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  // Styles
  const _fontSize = { ...(focus_view ? fonts.fontSize3 : fonts.fontSize4) };
  const _seperator = { borderBottom: "2px solid " + colors.blueDarkSecondary, width: mobile ? "80%" : undefined, margin: mobile ? "0 auto" : undefined }

  // #region | Texts
  let text_header = "";
  let text_noSelected = "";
  let text_clickPen = "";
  let text_deleteConfirm = "";

  switch (systemType) {
    case SystemTypes.freedaLf:
      text_header = t("locationFeederPages.sections.editSection", { defaultValue: "edit section" })
      text_noSelected = t("systemControls.general.noPensSelected", { defaultValue: "No pens selected" })
      text_clickPen = t("locationFeederPages.sections.clickPen", { defaultValue: "Click every pen you want to edit" })
      text_deleteConfirm = t("locationFeederPages.sections.deleteSectionConfirm", { defaultValue: "Are you sure you want to delete this section" })
    break;
    case SystemTypes.freedaEsf_WalkThrough:
      text_header = t("esfWalkThroughPages.newSection.editPen", { defaultValue: "edit pen" })
      text_noSelected = t("systemControls.general.noStationsSelected", { defaultValue: "no stations selected" })
      text_clickPen = t("esfWalkThroughPages.newSection.clickStationToEdit", { defaultValue: "Click every station you want to edit" })
      text_deleteConfirm = t("esfWalkThroughPages.newSection.deletePenConfirm", { defaultValue: "Are you sure you want to delete this pen" })
    break;
    default:
    return;
  }
  // #endregion

  return (<>
    <NewModal
      modalState={true}
      overrides={{
        mainContentPadding: MainContentPaddingTypes.secondary,
        secondaryContentScroll: true,
        mainContentScroll: true
      }}
      predefinedButtons={{ onClose: onClose }}
      header={text_header}
      subHeader={subHeader}
      mainContent={
        <div style={{ minHeight: mobile ? "100%" : undefined, height: mobile ? undefined : "100%", boxSizing: "border-box", gap: mobile ? "20px 0px" : "20px", display: "grid", gridTemplateColumns: mobile ? "1fr" : "350px 2px 280px", gridTemplateRows: mobile ? "fit-content(100%) 2px fit-content(100%)" : "100%" }}>
          <div style={{ gridArea: "1/1/2/2", height: "100%", display: "grid", gap: "10px", gridTemplateColumns: "100%", gridTemplateRows: mobile ? "60px 2px 250px 2px" : "50px 2px 1fr 2px" }}>

            {/* Input Area */}
            <div style={{ gridArea: "1/1/2/2", margin: "0 auto", boxSizing: "border-box", width: "100%", display: "flex", alignItems: "end", height: "44px", justifyContent: "space-between" }}>
              {inputArea}
              <NewButton
                content=""
                buttonType={ButtonTypes.glyphButton}
                startEnhancer={glyphs.Delete({ size: "25px" })}
                overrides={{ startEnhancerStyles: { height: "30px", display: "flex", alignItems: "center" } }}
                onClick={() => setDeleteModal(true)}
              />
            </div>
    
            <div style={{ gridArea: "2/1/3/2", ..._seperator }} />

            {/* Active Area */}
            <div style={{ gridArea: "3/1/4/2", height: "100%", overflow: "hidden", boxSizing: "border-box", display: "flex", flexDirection: "column", gap: "5px", overflowX: "hidden", overflowY: "auto", alignItems: "center", scrollbarColor: colors.blueDarkSecondary+colors.transparent}}>
              {(activeArea === undefined || activeArea?.length === 0) && 
                <p style={{ ..._fontSize, ...css.emptyPen, color: colors.greyLightTertiary, opacity: "0.5" }}> {`${text_noSelected}!`} </p>
              }
              <>{activeArea}</>
            </div>
            
            <div style={{ gridArea: "4/1/5/2", ..._seperator }} />
          </div>
    
          <div style={{ gridArea: mobile ? "2/1/3/2" : "1/2/2/3", borderLeft: mobile ? undefined : "2px solid " + colors.blueDarkSecondary, borderBottom: mobile ? "2px solid " + colors.blueDarkSecondary : undefined }} />
    
          {/* Select Area */}
          <div style={{ gridArea: mobile ? "3/1/4/3" : "1/3/2/4", height: "100%", width: "100%", display: "grid", gap: "5px", gridTemplateRows: mobile ? "30px 2px 250px 2px" : "22px 2px 1fr 2px", gridTemplateColumns: "100%" }}>
            <p style={{ gridArea: "1/1/2/2", ...fonts.paragraph, ..._fontSize, color: colors.greyLightTertiary, textAlign: "center" }}> {`${text_clickPen}:`} </p>
    
            <div style={{ gridArea: "2/1/3/2", ..._seperator }} />
            
            <div style={{
              gridArea: "3/1/4/2",
              transition: "filter 0.3s ease-in-out",
              overflowX: "hidden",
              overflowY: "auto",
              scrollbarColor: colors.blueDarkSecondary + colors.transparent,
              scrollbarWidth: "thin",
              display: "flex",
              padding: "10px",
              borderRadius: "10px",
              justifyContent: "center",
              alignItems: "flex-start"
            }}>
              <div style={{display: "block", margin: "auto"}}>
                <>{ selectArea }</>
              </div>
            </div>

            <div style={{ gridArea: "4/1/5/2", ..._seperator }} />

          </div>
        </div>
      }
      footerContent={ <NewButton 
        content={t("buttons.save", {defaultValue: "save"})}
        onClick={onSave}
      /> }
      footerMessage={ footerMessage }
    />
    
    {deleteModal && <ConfirmModal
      disclaimer={`${text_deleteConfirm}?`}
      onCancel={() => setDeleteModal(false)}
      onConfirm={async () => {
        setDeleteModal(false);
        await onDeleteConfirm();
      }}
      /> } 
  </>);
}